import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["children", "target", "replace", "href", "to", "onClick", "onMouseEnter", "onMouseLeave", "onPointerDown", "onFocus", "onBlur", "type", "params", "query", "prefetch", "state"];
import { createPath } from 'history';
import { createElement, forwardRef, useCallback, useEffect, useState } from 'react';
import { createRouterContext, generateLocationFromPath } from '../../common/utils';
import { useRouterStoreActions } from '../../controllers/router-store';
import { useTimeout } from '../../controllers/use-timeout';
import { getValidLinkType, handleNavigation } from './utils';
const PREFETCH_DELAY = 225;
const Link = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      children,
      target = '_self',
      replace = false,
      href = undefined,
      to = undefined,
      onClick = undefined,
      onMouseEnter = undefined,
      onMouseLeave = undefined,
      onPointerDown = undefined,
      onFocus = undefined,
      onBlur = undefined,
      type: linkType = 'a',
      params,
      query,
      prefetch = false,
      state = undefined
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const routerActions = useRouterStoreActions();
  const {
    schedule,
    cancel
  } = useTimeout(PREFETCH_DELAY);
  const validLinkType = getValidLinkType(linkType);
  const [route, setRoute] = useState(() => {
    if (to && typeof to !== 'string') {
      if ('then' in to) to.then(r => setRoute('default' in r ? r.default : r));else return to;
    }
  });
  const routeAttributes = {
    params,
    query,
    basePath: routerActions.getBasePath()
  };
  const linkDestination = href != null ? href : typeof to !== 'string' ? route && createPath(generateLocationFromPath(route.path, routeAttributes)) || '' : to;
  const IS_ABSOLUTE_LINK_REGEX = /^((?:(http|https):\/\/)|\/\/)/;
  const staticBasePath = href != null && !IS_ABSOLUTE_LINK_REGEX.test(href) || typeof to === 'string' ? routeAttributes.basePath : '';
  const triggerPrefetch = useCallback(() => {
    // ignore if async route not ready yet
    if (typeof to !== 'string' && !route) return;
    const context = typeof to !== 'string' && route ? createRouterContext(route, {
      params,
      query
    }) : null;
    routerActions.prefetchRoute(linkDestination, context);
    // omit params & query as already in linkDestination
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, linkDestination, routerActions]);
  useEffect(() => {
    if (prefetch === 'mount') {
      schedule(triggerPrefetch);
    }
    return cancel;
  }, [prefetch, schedule, cancel, triggerPrefetch]);
  const handleLinkPress = e => handleNavigation(e, {
    onClick,
    target,
    replace,
    routerActions,
    href: linkDestination,
    to: route && [route, {
      params,
      query
    }],
    state
  });
  const handleMouseEnter = e => {
    if (prefetch === 'hover') {
      schedule(triggerPrefetch);
    }
    onMouseEnter && onMouseEnter(e);
  };
  const handleMouseLeave = e => {
    if (prefetch === 'hover') {
      cancel();
    }
    onMouseLeave && onMouseLeave(e);
  };
  const handleFocus = e => {
    if (prefetch === 'hover') {
      schedule(triggerPrefetch);
    }
    onFocus && onFocus(e);
  };
  const handleBlur = e => {
    if (prefetch === 'hover') {
      cancel();
    }
    onBlur && onBlur(e);
  };
  const handlePointerDown = e => {
    if (prefetch === 'hover') {
      cancel();
      triggerPrefetch();
    }
    onPointerDown && onPointerDown(e);
  };
  return /*#__PURE__*/createElement(validLinkType, _extends({}, rest, {
    href: `${staticBasePath}${linkDestination}`,
    target,
    onClick: handleLinkPress,
    onKeyDown: handleLinkPress,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onPointerDown: handlePointerDown,
    ref
  }), children);
});
Link.displayName = 'Link';
export default Link;